import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { useLocalizedDayjs } from '@/languages/dayjs';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface DateValueInputProps {
  value: string;
  onChange: (value: string) => void;
  autoOpen?: boolean;
}

export function DateValueInput({ value, onChange, autoOpen }: DateValueInputProps) {
  const { t } = useTranslation('desktop_ui');
  const dayjs = useLocalizedDayjs();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Auto-open when signaled
  useEffect(() => {
    if (autoOpen) {
      setTimeout(() => {
        triggerRef.current?.click();
      }, 50);
    }
  }, [autoOpen]);

  const selectedDate = value ? new Date(value) : undefined;
  const displayValue = value ? dayjs(value).format('MMM D, YYYY') : '';

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Send as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className="h-full min-w-16 max-w-32 border-r border-primary-10 bg-transparent px-2 outline-none cursor-pointer hover:bg-primary-5 data-[state=open]:bg-primary-5 text-left truncate"
        >
          <P3 className={cn('truncate', value ? 'text-primary-100' : 'text-primary-60')}>
            {displayValue || t('components.table.selectDate')}
          </P3>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto rounded-2xl border border-primary-5 bg-white p-0 shadow-md"
      >
        <Calendar mode="single" selected={selectedDate} onSelect={handleSelect} />
      </PopoverContent>
    </Popover>
  );
}
